// Разбор замечаний периода: то, что мешает закрыть, поимённо и с переходом в строку.
//
// ## Почему поимённо, а не счётчиком
//
// «Двенадцать непроведённых траншей» — это работа, а не ответ. Финансовому директору нужно
// знать, у кого именно, чтобы либо провести их одним действием, либо пойти разбираться. Поэтому
// каждый раздел даёт список фамилий с суммами и переход в строку.
//
// ## Массовое проведение — необратимое действие, и подтверждение показывает ИТОГ
//
// Дата выплаты ставится один раз и назад не отматывается: сервер это прямо говорит. Поэтому
// подтверждение называет число траншей и сумму, а не спрашивает «вы уверены». Обратные записи
// в пачке считаются отдельной строкой: они уменьшают выплаченное, и слипание их с обычными
// траншами в одну сумму скрыло бы это.

import { useState } from 'react';

import { useLocation, useNavigate } from 'react-router';

import type { OfficialRowOut } from '@/shared/api/endpoints/official';
import { useAuthStore } from '@/shared/auth/store';
import { formatKopecks } from '@/shared/money/format';
import { can } from '@/shared/rbac/permissions';
import { buildSearch, parseSearch, pushPanel } from '@/shared/routing/search';
import { countLabel } from '@/shared/text/plural';
import { formatDateOnly } from '@/shared/time/format';
import * as Button from '@/shared/ui/button';
import { DateField, todayIso } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Modal from '@/shared/ui/modal';
import { ErrorState, Skeleton } from '@/shared/ui/screen-state';

import { PANEL_TRANCHES } from '../../route';
import { useGrid, usePayTranches } from '../queries';

import { вМоментВыплаты } from './context';

const EDIT_PERMISSION = 'core.official.edit';

export function ReviewPanel({ id }: { id: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  const права = useAuthStore((state) => state.permissions);
  const сетка = useGrid(id, {});
  const проведение = usePayTranches(id);

  const [дата, setДата] = useState(todayIso);
  const [окно, setОкно] = useState(false);
  const [запрет, setЗапрет] = useState<string | null>(null);

  if (сетка.error !== null && сетка.data === undefined) {
    return (
      <ErrorState
        subject='строки официального периода'
        error={сетка.error as never}
        onRetry={() => void сетка.refetch()}
      />
    );
  }
  if (сетка.data === undefined) {
    return (
      <div className='flex flex-col gap-2'>
        <Skeleton className='h-16 w-full rounded-lg' />
        <Skeleton className='h-40 w-full rounded-lg' />
      </div>
    );
  }

  const данные = сетка.data;
  const строки = данные.rows;

  const непроведённые = строки.filter((строка) => строка.planned !== 0);
  const переплаты = строки.filter((строка) => строка.balance < 0);
  const перенесённые = строки.filter((строка) => строка.is_carried_in);
  const отказы = строки.filter((строка) => строка.carry_refused_basis !== null);
  const безРеквизитов = строки.filter((строка) => строка.state === 'check_details');

  const кПроведению = строки.flatMap((строка) =>
    строка.tranches.filter((транш) => транш.paid_at === null && !транш.is_reversed),
  );
  const обычные = кПроведению.filter((транш) => транш.reversal_of_id === null);
  const обратные = кПроведению.filter((транш) => транш.reversal_of_id !== null);
  const суммаОбычных = обычные.reduce((итог, транш) => итог + транш.amount, 0);
  const суммаОбратных = обратные.reduce((итог, транш) => итог + транш.amount, 0);

  function открытьСтроку(строка: OfficialRowOut) {
    const состояние = parseSearch(location.search);
    void navigate({
      pathname: location.pathname,
      search: buildSearch(pushPanel(состояние, { view: PANEL_TRANCHES, id: строка.id })),
    });
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-1'>
        <h3 className='text-label-lg text-text-strong-950'>{данные.period.title}</h3>
        <span className='text-paragraph-xs text-text-sub-600'>
          {данные.review.nothing_to_check
            ? 'Проверять нечего: замечаний по периоду нет.'
            : данные.review.summary}
        </span>
      </div>

      {данные.review.nothing_to_check && (
        <p className='rounded-lg bg-bg-weak-50 px-3 py-2 text-paragraph-sm text-text-sub-600'>
          Непроведённых траншей, переплат, отказов от переноса и строк без реквизитов нет. Закрытию
          периода ничего не мешает.
        </p>
      )}

      {запрет !== null && (
        <p className='rounded-lg bg-bg-weak-50 px-3 py-2 text-paragraph-xs text-warning-base'>
          {запрет}
        </p>
      )}

      {кПроведению.length > 0 && (
        <section className='flex flex-col gap-2'>
          <h4 className='tabular-nums text-label-sm text-text-strong-950'>
            Заведено, но не проведено: {данные.review.unpaid_tranches} на{' '}
            {formatKopecks(данные.review.unpaid_total)}
          </h4>
          <p className='text-paragraph-xs text-text-sub-600'>
            Пока дата выплаты не стоит, остаток эти деньги не уменьшают. Закрыть период с ними
            нельзя: в закрытом периоде провести их будет нечем.
          </p>
          <DateField
            id='official-bulk-paid-at'
            label='Дата выплаты'
            value={дата}
            onChange={setДата}
          />
          <Button.Root
            variant='primary'
            mode='filled'
            size='xsmall'
            onClick={() => {
              if (!can(права, EDIT_PERMISSION) || !данные.can_edit) {
                setЗапрет(
                  данные.blocked_reason ??
                    'Проводить транши может финансовый отдел или администратор.',
                );
                return;
              }
              setЗапрет(null);
              setОкно(true);
            }}
          >
            Провести все · {countLabel(кПроведению.length, ['транш', 'транша', 'траншей'])}
          </Button.Root>
          <СписокСтрок строки={непроведённые} поле='planned' onOpen={открытьСтроку} />
        </section>
      )}

      {переплаты.length > 0 && (
        <section className='flex flex-col gap-2'>
          <h4 className='tabular-nums text-label-sm text-text-strong-950'>
            Переплаты: {данные.review.overpaid_rows} на{' '}
            {formatKopecks(данные.review.overpaid_total)}
          </h4>
          <p className='text-paragraph-xs text-text-sub-600'>
            Выдали больше начисленного. Переносом это не является и в следующий период не уйдёт —
            разбирается вручную.
          </p>
          <СписокСтрок строки={переплаты} поле='balance' onOpen={открытьСтроку} безЗнака />
        </section>
      )}

      {перенесённые.length > 0 && (
        <section className='flex flex-col gap-2'>
          <h4 className='tabular-nums text-label-sm text-text-strong-950'>
            Пришло переносом: {данные.review.carried_in_rows} на{' '}
            {formatKopecks(данные.review.carried_in_total)}
          </h4>
          <p className='text-paragraph-xs text-text-sub-600'>
            Это непогашенный остаток прошлого периода. Процент по исполнительному документу к нему
            не применяется: удержание уже сделали там, где деньги начислялись.
          </p>
          <СписокСтрок строки={перенесённые} поле='payable' onOpen={открытьСтроку} />
        </section>
      )}

      {отказы.length > 0 && (
        <section className='flex flex-col gap-2'>
          <h4 className='tabular-nums text-label-sm text-text-strong-950'>
            Отказы от переноса: {данные.review.refused_rows} на{' '}
            {formatKopecks(данные.review.refused_total)}
          </h4>
          <p className='text-paragraph-xs text-text-sub-600'>
            Эти деньги в новый период не уйдут. Основание записано в журнал.
          </p>
          <СписокСтрок строки={отказы} поле='balance' onOpen={открытьСтроку} />
        </section>
      )}

      {безРеквизитов.length > 0 && (
        <section className='flex flex-col gap-2'>
          <h4 className='text-label-sm text-text-strong-950'>
            Без реквизитов: {данные.review.no_details_rows}
          </h4>
          <p className='text-paragraph-xs text-text-sub-600'>
            Строки в состоянии «уточнить реквизиты». Закрытию они не мешают, но деньги по ним не
            уйдут.
          </p>
          <СписокСтрок строки={безРеквизитов} поле='balance' onOpen={открытьСтроку} />
        </section>
      )}

      <Modal.Root
        open={окно}
        onOpenChange={(следующее) => {
          setОкно(следующее);
          if (!следующее) {
            // Иначе окно, открытое второй раз, показывает прошлый итог и не рисует кнопку
            // «провести»: часть траншей могла уйти в пропущенные, и их надо добить.
            проведение.reset();
          }
        }}
      >
        <Modal.Content className='max-w-md'>
          <Modal.Header title='Отметить транши выплаченными?' />
          <Modal.Body>
            <div className='flex flex-col gap-2 text-paragraph-sm text-text-sub-600'>
              <p className='text-text-strong-950'>
                {countLabel(обычные.length, ['транш', 'транша', 'траншей'])} на{' '}
                {formatKopecks(суммаОбычных)}, дата выплаты {formatDateOnly(дата)}.
              </p>
              {обратные.length > 0 && (
                <p>
                  Из них{' '}
                  {countLabel(обратные.length, [
                    'обратная запись',
                    'обратные записи',
                    'обратных записей',
                  ])}{' '}
                  на {formatKopecks(суммаОбратных)} — они уменьшат выплаченное и вернут остаток.
                </p>
              )}
              <p>
                Дата выплаты ставится один раз и назад не отматывается. Ошиблись — снимать придётся
                сторно, с основанием.
              </p>
              {проведение.data !== undefined && (
                <p className='text-text-strong-950'>
                  Проведено {проведение.data.paid}
                  {проведение.data.already_paid > 0 && (
                    <>, уже были отмечены раньше: {проведение.data.already_paid}</>
                  )}
                  {проведение.data.skipped.length > 0 && (
                    <>
                      . Не легло {проведение.data.skipped.length}:{' '}
                      {проведение.data.skipped
                        .map((пункт) => `${пункт.label} — ${пункт.reason}`)
                        .join('; ')}
                    </>
                  )}
                  .
                </p>
              )}
              {проведение.error !== null && <ErrorAlert error={проведение.error} />}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button.Root
              variant='neutral'
              mode='stroke'
              size='small'
              onClick={() => {
                setОкно(false);
                проведение.reset();
              }}
            >
              {проведение.data === undefined ? 'Отмена' : 'Закрыть окно'}
            </Button.Root>
            {проведение.data === undefined && (
              <Button.Root
                variant='primary'
                mode='filled'
                size='small'
                onClick={() =>
                  проведение.mutate({
                    trancheIds: кПроведению.map((транш) => транш.id),
                    paidAt: вМоментВыплаты(дата),
                  })
                }
              >
                {проведение.isPending ? 'Проводим…' : 'Провести'}
              </Button.Root>
            )}
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
    </div>
  );
}

function СписокСтрок({
  строки,
  поле,
  onOpen,
  безЗнака = false,
}: {
  строки: readonly OfficialRowOut[];
  поле: 'planned' | 'balance' | 'payable';
  onOpen: (строка: OfficialRowOut) => void;
  /**
   * Показывать величину без знака.
   *
   * ⚠️ Нужно ровно там, где направление уже сказано СЛОВОМ. Заголовок «Переплаты: 13 на
   * 15 127,11» стоял над строками с минусом: сверху величина, снизу те же деньги с обратным
   * знаком, и человек читал это как два разных числа. Минус в такой паре не уточняет, а
   * спорит с заголовком.
   */
  безЗнака?: boolean;
}) {
  return (
    <ul className='flex flex-col'>
      {строки.map((строка) => (
        <li key={строка.id}>
          <button
            type='button'
            onClick={() => onOpen(строка)}
            className='flex w-full items-baseline justify-between gap-2 rounded-lg px-2 py-1 text-left hover:bg-bg-weak-50'
          >
            <span className='flex min-w-0 flex-col'>
              <span className='truncate text-paragraph-sm text-text-strong-950'>
                {строка.employee_name}
              </span>
              {/* Одной фразой, почему строка здесь, — её собирает сервер. Своя, собранная из
                  чисел заново, разойдётся с ней на первой же правке. */}
              {строка.review_reason !== null && (
                <span className='truncate text-paragraph-xs text-text-sub-600'>
                  {строка.review_reason}
                </span>
              )}
            </span>
            <span className='shrink-0 tabular-nums text-paragraph-sm text-text-sub-600'>
              {formatKopecks(безЗнака ? Math.abs(строка[поле]) : строка[поле])}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
